import { useMemo, useState } from "react";

import { CreateEmptyPageModule } from "../../../../../../connection/page-modules";
import { addSessionPageModule, updatePageModule } from "../../../../../../store/actions/admin/create-event/session";
import { useAppDispatch, useTypedSelector } from "../../../../../../store/reducers/use-typed-selector";
import { PageModule, LanguagesAbbr, Session } from "../../../../../../types/working-model";
import { showAlert } from "../../../../../general-ui/alert/alert-service";
import CustomHTML from "../../../../../general-ui/edit-html/edit-html";
import ModalComponent from "../../../../../general-ui/modal/modal";
import WaitingIndicator from "../../../../../general-ui/waiting-indicator/waiting-indicator";
import CustomOptions from "../../../session/navigation/custom-options/custom-options";
import SessionModuleGroupingListV2 from './session-module-grouping-list-v2';
import EditorLanguageSelector from "@admin/create/translations/editor-language-selector";
import { availableLanguages } from "../../editor/agenda-editor/select-languages";
import SessionSettingsV2 from "./session-settings-v2";
import { selectEditorNavV3 } from "store/selectors/feature-flags";

function SessionContentV2(): JSX.Element {
	const isV3 = useTypedSelector(selectEditorNavV3);
	const token = useTypedSelector((state) => state.AuthReducer.token);
	const workingSession = useTypedSelector((state) => state.CreateSessionReducer.workingSession);
	const workingEvent = useTypedSelector(state => state.CreateEventReducer.workingEvent);
	const [addModuleOpen, setAddModuleOpen] = useState(false);
	const [blankModalOpen, setBlankModalOpen] = useState(false);
	const [workingBlankModule, setWorkingBlankModule] = useState<PageModule | null>(null);

	const dispatch = useAppDispatch();

	const sessionLanguages: LanguagesAbbr[] = useMemo(() => (
		availableLanguages([workingSession as Session], undefined).map(lang => lang.code) as LanguagesAbbr[]
	), [workingSession]);

	function closeBlankModule() {
		setBlankModalOpen(false);
	}

	function updateWorkingBlankModule(code: string) {
		setWorkingBlankModule((module: PageModule | null) => {
			if (!module) { return null; }
			return {
				...module,
				content: {
					html: code,
				},
			};
		});
	}

	async function finishedEditingBlank() {
		if (!token || !workingEvent || !workingSession) return;

		try {
			if (workingBlankModule) {
				if (workingBlankModule.id) {
					dispatch(updatePageModule(workingBlankModule));
				} else {
					const newModule = await CreateEmptyPageModule(token, {
						...workingBlankModule,
						channel: workingEvent.channel,
						event: workingEvent.event,
						session: workingSession.session
					});
					dispatch(addSessionPageModule(newModule));
				}
			}
		} catch (error) {
			console.error(error);
			showAlert({
				message: "Error Creating Module",
				description: "We ran into an error creating the module. Please try again.",
				duration: 5000,
				type: "error",
			});
		}

		closeBlankModule();
	}

	if (!workingSession) return <WaitingIndicator />;

	return (
		<>
			<EditorLanguageSelector
				className="session-language-selector"
				languagesOverride={sessionLanguages}
			/>

			{isV3 && (
				<SessionSettingsV2 />
			)}

			<SessionModuleGroupingListV2 />

			<CustomOptions
				addModuleOpen={addModuleOpen}
				blankModalOpen={blankModalOpen}
				setAddModuleOpen={setAddModuleOpen}
				setBlankModalOpen={setBlankModalOpen}
				workingSession={workingSession}
			/>
			<ModalComponent
				cancellable={true}
				closeable={false}
				open={blankModalOpen}
				onRequestClose={closeBlankModule}
				title={"Blank Module"}
				footer={
					<>
						<button onClick={closeBlankModule} style={{ marginRight: 16 }}>
							Cancel
						</button>
						<button onClick={finishedEditingBlank} className="lemonade">
							Done
						</button>
					</>
				}
			>
				<div>
					<label className="subtitle">Add custom HTML Code</label>

					<CustomHTML
						defaultValue={workingBlankModule?.content.html ?? ""}
						onChange={updateWorkingBlankModule}
					/>
				</div>
			</ModalComponent>
		</>
	);
}

export default SessionContentV2;
